import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useSiteSettings } from '@gn-audio/jss-components';
import { DEFAULT_LOCALE } from '@gn-audio/jss-core';

import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

import { VideoRoiCalculatorFovSelector } from './VideoRoiCalculatorFovSelector';
import VideoRoiCalculatorRangeField from './VideoRoiCalculatorRangeField';
import VideoRoiCalculatorTextField from './VideoRoiCalculatorTextField';
import { CalculatorResults, CalculatorState, FovImage, UnitType } from './videoRoiCalculatorTypes';
import { VideoRoiCalculatorUnitSwitcher } from './VideoRoiCalculatorUnitSwitcher';
import { convertFromMetersToUnit } from './videoRoiCalculatorUtilities';

const initialCalculatorState: CalculatorState = {
  unit: 'm',
  numberOfRooms: 10,
  roomWidth: 3,
  maxRoomWidthInMeters: 5,
  rentCostPerSquareUnit: 50,
  tableWidthInMeters: 1.25,
  fov: 72,
};

export const FOV_IMAGES: FovImage[] = [
  { fov: 72, id: '67B0E8CDB0BF44F58BE3BCC4C087D77F' },
  { fov: 110, id: '8DB22FC2DFEA4B66B4BE35B963C2B11F' },
  { fov: 180, id: '09C3323A017B4FDA946C4226BD03AAA3' },
];

const DEFAULT_CURRENCY = 'USD';

const VideoRoiCalculator: FC = () => {
  const [calculator, setCalculator] = useState<CalculatorState>(initialCalculatorState);
  const [currencyFormatter, setCurrencyFormatter] = useState<Intl.NumberFormat>();
  const { t } = useFallbackTranslation();

  const router = useRouter();
  const { sfcc } = useSiteSettings();
  useEffect(() => {
    setCurrencyFormatter(
      new Intl.NumberFormat(router.locale || DEFAULT_LOCALE, {
        style: 'currency',
        currency: sfcc?.currency || DEFAULT_CURRENCY,
        minimumFractionDigits: 0,
      })
    );
  }, [router.locale, sfcc?.currency]);

  const getMaxWidthInCurrentUnit = useCallback(
    (unit: UnitType): number => {
      return Math.trunc(convertFromMetersToUnit(calculator.maxRoomWidthInMeters, unit));
    },
    [calculator.maxRoomWidthInMeters]
  );

  const handleUnitChange = useCallback(
    (unit: UnitType) => {
      setCalculator((prev) => {
        const maxWidthInCurrentUnit = getMaxWidthInCurrentUnit(unit);

        return {
          ...prev,
          unit,
          roomWidth: Math.ceil(maxWidthInCurrentUnit / 2),
        };
      });
    },
    [getMaxWidthInCurrentUnit]
  );

  const results = useMemo<CalculatorResults>(() => {
    const calculateDistanceFromWall = (): number => {
      const fovInRadians = calculator.fov * (Math.PI / 180);
      const tableWidth = Math.min(
        convertFromMetersToUnit(calculator.tableWidthInMeters, calculator.unit),
        calculator.roomWidth
      );
      return tableWidth / 2 / Math.tan(fovInRadians / 2);
    };

    const calculateWastedSpace = (distanceFromWall: number): number =>
      calculator.roomWidth * distanceFromWall;

    const distanceFromWall = calculateDistanceFromWall();
    const wastedSpace = calculateWastedSpace(distanceFromWall);

    const wastedCostPerRoom = calculator.rentCostPerSquareUnit * wastedSpace;
    const monthlyCost = Math.trunc(wastedCostPerRoom * calculator.numberOfRooms);
    const yearlyCost = monthlyCost * 12;

    return {
      wastedSpace: wastedSpace.toFixed(2),
      monthlyCost: currencyFormatter?.format(monthlyCost) || '',
      yearlyCost: currencyFormatter?.format(yearlyCost) || '',
    };
  }, [
    calculator.rentCostPerSquareUnit,
    calculator.numberOfRooms,
    calculator.fov,
    calculator.tableWidthInMeters,
    calculator.unit,
    calculator.roomWidth,
    currencyFormatter,
  ]);

  return (
    <div className="relative flex w-full flex-col md:block md:h-[1200px]">
      <div className="relative left-0 top-0 z-10 order-2 aspect-[16/10] w-full md:absolute md:h-full">
        {FOV_IMAGES.map((image, index) => {
          const selectedIndex = FOV_IMAGES.findIndex((img) => img.fov === calculator.fov);
          return (
            <div
              key={image.fov}
              className={classNames(
                `absolute left-0 top-0 size-full transition-opacity duration-300 ease-in-out`,
                {
                  'opacity-100': index <= selectedIndex,
                  'opacity-0': index > selectedIndex,
                }
              )}
              style={{
                zIndex: index + 1,
              }}
            >
              <Image
                src={`/-/media/${image.id}.ashx`}
                alt={t(`system.P50Roi${image.fov}AltText`)}
                fill
                sizes="(max-width: 1920px) 100vw, 1920px"
                priority={index === 0}
                quality={90}
                className="object-cover object-center"
              />
            </div>
          );
        })}
      </div>

      <div className="relative z-20 order-3 w-full bg-green-700 shadow-xl md:absolute md:right-16 md:top-1/2 md:max-w-[400px] md:-translate-y-1/2 md:rounded-[24px_0_24px_24px] xl:right-32">
        <VideoRoiCalculatorUnitSwitcher
          activeUnit={calculator.unit}
          onUnitChange={handleUnitChange}
          className="absolute bottom-full right-0 z-0"
        />

        <div className="relative z-10 bg-yellow p-8 md:rounded-[24px_0_24px_24px]">
          <VideoRoiCalculatorRangeField
            title={t('system.P50RoiNumberOfRooms')}
            value={calculator.numberOfRooms}
            onChange={(value) => setCalculator((prev) => ({ ...prev, numberOfRooms: value }))}
            min={1}
            max={100}
            id="number-of-rooms"
            name="number-of-rooms"
          />

          <VideoRoiCalculatorRangeField
            title={t('system.P50RoiRoomWidth')}
            value={calculator.roomWidth}
            onChange={(value) => setCalculator((prev) => ({ ...prev, roomWidth: value }))}
            min={1}
            max={getMaxWidthInCurrentUnit(calculator.unit)}
            id="room-width"
            name="room-width"
            unitSuffix={`(${calculator.unit})`}
          />

          <VideoRoiCalculatorTextField
            title={t('system.P50RoiRentCost')}
            value={calculator.rentCostPerSquareUnit}
            onChange={(value) =>
              setCalculator((prev) => ({ ...prev, rentCostPerSquareUnit: value }))
            }
            max={5000}
            id="room-cost-per-unit"
            name="room-cost-per-unit"
            placeholder="50"
            unitSuffix={
              <>
                /{' '}
                <span>
                  {calculator.unit}
                  <sup>2</sup>
                </span>
              </>
            }
          />
        </div>

        <div className="p-8 text-white">
          <div className="mb-4">
            <h2 className="mb-2 mt-3 text-lg font-bold"> {t('system.P50RoiWastedSpace')}</h2>
            <div className="text-2xl font-bold">
              {`${results.wastedSpace} `}
              <span>
                {calculator.unit}
                <sup>2</sup>
              </span>
            </div>
          </div>

          <div className="border-t border-dashed border-blue-500">
            <h2 className="mb-2 mt-3 text-lg font-bold">{t('system.P50RoiWastedSpaceValue')}</h2>
            {[
              { value: results.monthlyCost, translationKey: 'system.P50RoiPerMonth' },
              {
                value: results.yearlyCost,
                translationKey: 'system.P50RoiPerYear',
                className: 'mt-4',
              },
            ].map(({ value, translationKey, className }) => (
              <div key={translationKey} className={className}>
                <div className="text-2xl font-bold">{value}</div>
                <span className="italic"> {t(translationKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <VideoRoiCalculatorFovSelector
        selectedFov={calculator.fov}
        onFovChange={(fov) => setCalculator((prev) => ({ ...prev, fov }))}
        className="z-20 order-1"
      />
    </div>
  );
};
export default VideoRoiCalculator;
